import { Component, computed, inject, OnInit, signal } from '@angular/core';

import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { Store } from '@ngxs/store';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { GenericTreeEntity } from '../generic-tree.entity';
import { ConsumerEntity } from '../../consumer/consumer.entity';
import { ConsumerEntityService, ConsumerEntityState } from '../../consumer/services/consumer.entity.service';
import { GlobalMetaDataEntityService, GlobalMetaDataEntityState } from '../../global-metadata/services/global-metadata.entity.service';
import { GlobalMetaDataEntity } from '../../global-metadata/global-metadata.entity';
import { TranslocoModule } from '@jsverse/transloco';
import { ObjectConsumerMetadataComponent } from '../../consumer/object-consumer-metadata/object-consumer-metadata.component';

@Component({
  selector: 'pixl-metadata-assignment-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    MatButtonModule,
    ReactiveFormsModule,
    TranslocoModule,
    ObjectConsumerMetadataComponent,
  ],
  templateUrl: './metadata-assignment-dialog.component.html',
  styleUrl: './metadata-assignment-dialog.component.scss'
})
export class MetadataAssignmentDialogComponent implements OnInit {
  private dialogRef = inject(MatDialogRef<MetadataAssignmentDialogComponent>);
  public data = inject(MAT_DIALOG_DATA) as { node: GenericTreeEntity };
  private store = inject(Store);
  private fb = inject(FormBuilder);
  private globalMetadataService = inject(GlobalMetaDataEntityService);

  private consumerService = inject(ConsumerEntityService);

  form = this.fb.group({
    consumer: [this.data.node.consumerId],
    consumerData: this.fb.group(
      Object.fromEntries(
        Object.entries(this.data.node.consumerData || {}).map(([key, value]) => [
          key,
          // FormBuilder treats a bare array as `[value, validators]`, so array values must be wrapped.
          Array.isArray(value) ? [value] : value,
        ])
      )
    )
  });

  consumer = signal<ConsumerEntity | undefined>(undefined);

  allMetadata = signal<GlobalMetaDataEntity[]>([]);
  activeMetadata = computed(() => {
    const currentData = this.data.node.consumerData || {};
    return this.allMetadata().filter(m =>
      m.fieldConfig?.some(f => Object.prototype.hasOwnProperty.call(currentData, f.name))
    );
  });

  async ngOnInit(): Promise<void> {
    const promises: Promise<unknown>[] = [this.loadMetadata()];
    if (this.data.node.consumerId) {
      promises.push(this.consumerService.ensureSingleLoaded(this.data.node.consumerId));
    }
    await Promise.all(promises);

    if (this.data.node.consumerId) {
      const consumer = this.store.selectSnapshot(ConsumerEntityState.byId)(this.data.node.consumerId);
      this.consumer.set(consumer);
    }
  }

  async loadMetadata() {
    await this.globalMetadataService.ensureLoaded({ page: { size: 1000 } });

    const entities = this.store.selectSnapshot(GlobalMetaDataEntityState.all);

    const models = [...entities];
    models.sort((a, b) => a.name.localeCompare(b.name));

    this.allMetadata.set(models);
  }

  onSave(): void {
    const consumerData = this.form.get('consumerData')?.value || {};

    this.dialogRef.close({
      ...this.data.node,
      consumerData: {
        ...this.data.node.consumerData,
        ...consumerData
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
